import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// Configure plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Set default timezone to JST
export const JST_TIMEZONE = 'Asia/Tokyo';

/**
 * Convert date string to UNIX timestamp
 * @param dateStr ISO format date string
 * @param precision Precision ('second' | 'millisecond')
 * @returns UNIX timestamp (in seconds or high precision)
 */
export function dateToUnixTimestamp(dateStr: string, precision: 'second' | 'millisecond' = 'second'): number {
  const timestamp = new Date(dateStr).getTime();
  return precision === 'second' ? Math.floor(timestamp / 1000) : timestamp / 1000;
}

/**
 * Convert standard timestamp (in seconds) to ISO string
 */
export function timestampToISOString(timestamp: number): string {
  return new Date(timestamp * 1000).toISOString();
}

/**
 * Interface representing a date range
 */
export interface DateRange {
  since: string;
  until: string;
}

/**
 * Get date range (from 00:00:00 to 00:00:00 of the next day) for the specified date
 */
export function getDateRange(date?: string): DateRange {
  const now = date ? dayjs(date) : dayjs();

  // Format date in UTC time
  const formatter = (d: dayjs.Dayjs) => {
    return d.utc().toISOString();
  };

  // 00:00:00 of the current day
  const since = now.hour(0).minute(0).second(0).millisecond(0);

  // 00:00:00 of the next day
  const until = since.add(1, 'day');

  return {
    since: formatter(since),
    until: formatter(until),
  };
}

/**
 * Convert date string to standard format
 */
export function formatDate(dateStr: string | Date): string {
  return dayjs(dateStr).utc().toISOString();
}

/**
 * Get start time of a date (00:00:00 of that day)
 */
export function getStartOfDay(dateStr: string): string {
  return dayjs(dateStr).hour(0).minute(0).second(0).millisecond(0).utc().toISOString();
}

/**
 * Get end time of a date (23:59:59.999 of that day)
 */
export function getEndOfDay(dateStr: string): string {
  return dayjs(dateStr).hour(23).minute(59).second(59).millisecond(999).utc().toISOString();
}

/**
 * Safely parse date value
 */
export function safeParseDate(value: unknown): Date | null {
  if (value === null || value === undefined) return null;

  try {
    // Clearly separate processing by type
    if (typeof value === 'number') {
      return new Date(value * 1000);
    }

    if (typeof value === 'string') {
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date;
    }

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    return null;
  } catch (e) {
    console.warn(`Invalid date value: ${value}`, e);
    return null;
  }
}

/**
 * Format date and time for display
 * @param dateTimeString ISO 8601 format string
 * @returns Formatted date/time string (e.g., "2025-03-10 17:15:00")
 */
export const formatDateTimeForDisplay = (dateTimeString?: string): string => {
  if (!dateTimeString) return '-';
  return formatJST(dateTimeString, 'YYYY-MM-DD HH:mm:ss');
};

/**
 * Convert ISO 8601 format (UTC) string to JST
 * @param isoString ISO 8601 format string (e.g., "2025-03-10T08:15:00Z")
 * @returns dayjs object in JST
 */
export const toJST = (isoString: string) => {
  return dayjs(isoString).tz(JST_TIMEZONE);
};

/**
 * Convert JST date/time to string in specified format
 * @param isoString ISO 8601 format string
 * @param format Output format (default: 'YYYY-MM-DD HH:mm:ss')
 * @returns Formatted date/time string
 */
export const formatJST = (isoString: string, format = 'YYYY-MM-DD HH:mm:ss') => {
  return toJST(isoString).format(format);
};

/**
 * Convert JST date/time to ISO 8601 format (UTC)
 * @param jstDate JST date/time (string or Date)
 * @returns ISO 8601 format string
 */
export const toUTC = (jstDate: string | Date) => {
  return dayjs(jstDate).tz(JST_TIMEZONE).utc().toISOString();
};

/**
 * Get current time in JST
 * @returns dayjs object of current time in JST
 */
export const nowJST = () => {
  return dayjs().tz(JST_TIMEZONE);
};

/**
 * Check if a date is valid
 * @param date Date to check
 * @returns true if valid date, false otherwise
 */
export const isValidDate = (date: string | Date) => {
  return dayjs(date).isValid();
};

/**
 * Calculate seconds between two datetime strings
 * @param startTime Start time (ISO 8601 format)
 * @param endTime End time (ISO 8601 format)
 * @returns Seconds
 */
export function calculateDurationInSeconds(startTime: string, endTime: string): number {
  const start = dayjs(startTime);
  const end = dayjs(endTime);

  // Get difference in milliseconds and convert to seconds
  return end.diff(start, 'second');
}

/**
 * Get 00:00 (start of day) of the specified date in UTC time
 * Ensures that 00:00 to 24:00 of the specified date is always covered, regardless of specific timezone
 *
 * @param date Target date (Date object)
 * @param timezone Timezone (uses browser's timezone if omitted)
 * @returns ISO string of UTC time corresponding to 00:00 of the specified date
 */
export const getStartOfDayInUTC = (date: Date, timezone?: string): string => {
  // Use browser's timezone if not specified
  const tz = timezone || dayjs.tz.guess();

  // Create dayjs object representing 00:00:00 of the date in the specified timezone
  const startOfDay = dayjs(date).tz(tz).startOf('day');

  // Convert to UTC time and return as ISO string
  return startOfDay.utc().toISOString();
};
